import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { FaSearch } from "react-icons/fa";
import { formatDate } from "../../utils/dateFormatter";
import { useNavigate } from "react-router-dom";
import GradientBanner from "../../componentes/Banner";
import PageHeader from "../../componentes/PageHeader";
import Sidebar from "../../componentes/Sidebar";
import MetaTags from "../../componentes/MetaTags";
import { useTranslation } from "react-i18next";
import LoadingSkeleton from "../../componentes/LoadingSkeleton";
import { Search } from "lucide-react";
import { motion } from "framer-motion";
import { fetchNews, fetchNewsCategories } from '../../api/news';
import "./ckeditor-content.css";
import { API_BASE_URL } from '../../api/config';
import Pagination from "../../componentes/Pagination";
import {
  faNewspaper,
  faRss,
  faBell,
  faEnvelope,
  faBullhorn,
  faGlobe,
  faCalendarAlt,
  faExclamationTriangle,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";

const baseUrl = API_BASE_URL;

// Strip HTML tags for card description previews
const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

// API fetch functions for sidebar
const fetchEvents = async (lang) => {
  const response = await fetch(`${baseUrl}/api/events?lang=${lang}`);
  return response.json();
};

const fetchAlerts = async (lang) => {
  const response = await fetch(`${baseUrl}/api/alerts?lang=${lang}`);
  return response.json();
};

const News = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [showSearchBox, setShowSearchBox] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // Fetch news using useQuery with backend pagination
  const {
    data: newsData,
    isLoading: newsLoading,
    error: newsError,
  } = useQuery({
    queryKey: ["news", {
      lang,
      page: currentPage,
      category: selectedCategory !== "all" ? selectedCategory : undefined,
      search: debouncedSearch || undefined
    }],
    queryFn: () => fetchNews({
      lang,
      page: currentPage,
      category: selectedCategory !== "all" ? selectedCategory : undefined,
      search: debouncedSearch || undefined
    }),
    keepPreviousData: true,
    staleTime: 0,
    cacheTime: 300000,
  });

  // Fetch categories using useQuery
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ["newsCategories", lang],
    queryFn: () => fetchNewsCategories(lang),
  });

  // Fetch events and alerts for sidebar
  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ["events", lang],
    queryFn: () => fetchEvents(lang),
  });

  const { data: alertsData, isLoading: alertsLoading } = useQuery({
    queryKey: ["alerts", lang],
    queryFn: () => fetchAlerts(lang),
  });

  const newsItems = newsData?.results || [];
  const totalPages = newsData ? Math.ceil(newsData.count / 10) : 0;
  const categories = categoriesData || [];
  const events = eventsData?.results || eventsData || [];
  const alerts = alertsData?.results || alertsData || [];
  const loading = newsLoading || categoriesLoading || eventsLoading || alertsLoading;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, debouncedSearch]);

  // Debounced search effect - proper implementation
  useEffect(() => {
    if (searchQuery === debouncedSearch) return; // Prevent unnecessary updates

    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Filter and paginate news - now handled by backend
  const paginatedNews = newsItems;

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const truncateText = (text, maxLength) => {
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  if (loading) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("news.title")}
          description={t("news.subtitle")}
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("header.news"), link: null },
          ]}
          backgroundIcons={[
            faNewspaper,
            faRss,
            faBell,
            faEnvelope,
            faBullhorn,
            faGlobe,
          ]}
        />
        <FullscreenSpinner />
      </>
    );
  }

  if (newsError) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("news.title")}
          description={t("news.subtitle")}
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("header.news"), link: null },
          ]}
          backgroundIcons={[
            faNewspaper,
            faRss,
            faBell,
            faEnvelope,
            faBullhorn,
            faGlobe,
          ]}
        />
        <div className="flex justify-center items-center min-h-[400px]">
          <p className="text-red-500 font-nunito-sans text-lg">
            {t("news.error", { message: newsError.message })}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <MetaTags title="News — Ethio-CERT" description="Latest news and updates from Ethio-CERT." url="https://ethiocert.gov.et/news" />
      <PageHeader />
      <GradientBanner
        title={t("news.title")}
        description={t("news.subtitle")}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("header.news"), link: null },
        ]}
        backgroundIcons={[
          faNewspaper,
          faRss,
          faBell,
          faEnvelope,
          faBullhorn,
          faGlobe,
        ]}
      />
      <div className="min-h-screen">
        <div className="flex flex-col max-xl:px-4 py-4 md:py-8 px-dynamic">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 lg:gap-6 mt-6">
            <div className="md:col-span-2">
              <div className="flex flex-col mb-8 mt-4 text-nowrap bg-gray-100 rounded-l-lg rounded-r-lg font-bold opacity-85 text-[#20273D]">
                <div className="flex gap-4 lg:pr-1">
                  <button
                    onClick={() => {
                      setSelectedCategory("all");
                    }}
                    className={`py-2.5 rounded-lg transition-all font-bold opacity-100 text-[#20273D] ${selectedCategory === "all"
                      ? "px-3 bg-primary text-white"
                      : "bg-gray-100 hover:bg-gray-200"
                      }`}
                  >
                    All News
                  </button>
                  <div
                    className="flex items-center gap-4 overflow-x-auto rounded-l-lg"
                    style={{
                      scrollbarWidth: "thin",
                      WebkitOverflowScrolling: "touch",
                    }}
                  >
                    {categories.map((category) => (
                      <button
                        key={category.slug}
                        onClick={() => {
                          setSelectedCategory(category.slug);
                        }}
                        className={`px-4 h-full rounded-lg whitespace-nowrap transition-all ${selectedCategory === category.slug
                          ? "bg-primary text-white"
                          : "bg-gray-100 hover:bg-gray-200"
                          }`}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setShowSearchBox(!showSearchBox)}
                    className="rounded-r-lg p-2 ml-auto hover:bg-gray-200 transition-all"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-8 text-gray-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {showSearchBox && (
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search news..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              )}

              <h1 className="text-2xl font-bold opacity-100 text-[#20273D] mb-6">
                {selectedCategory === "all"
                  ? t("news.all")
                  : t("common.category") +
                  "-" +
                  categories.find((cat) => cat.slug === selectedCategory)
                    ?.name}
              </h1>

              <div className="space-y-6">
                {paginatedNews.length > 0 ? (
                  paginatedNews.map((item) => {
                    const hasMedia = item.image_file || item.image || (item.media_type === "video" && (item.video_file || item.video)) || (item.image && (item.image.includes('youtube.com') || item.image.includes('youtu.be') || item.image.includes('vimeo.com')));

                    return (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="group bg-white rounded-xl border border-gray-200 hover:border-primary/30 hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
                        onClick={() => navigate(`/news/${item.slug}`)}
                      >
                        <div className="flex max-sm:flex-col">
                          {/* Always show media container with fallback */}
                          <div className="w-2/5 aspect-[16/10] max-sm:w-full flex-shrink-0 relative overflow-hidden">
                            {hasMedia ? (
                              item.media_type === "video" || (item.image && (item.image.includes('youtube.com') || item.image.includes('youtu.be') || item.image.includes('vimeo.com'))) ? (
                                <div className="relative h-full bg-gray-900 flex items-center justify-center">
                                  {item.video_file ? (
                                    <video
                                      src={item.video_file}
                                      className="w-full h-full object-cover"
                                      autoPlay
                                      muted
                                      loop
                                      playsInline
                                      controls
                                    />
                                  ) : item.video ? (
                                    <iframe
                                      src={(() => {
                                        if (item.video.includes("youtube.com") || item.video.includes("youtu.be")) {
                                          const videoId = item.video.includes("youtube.com")
                                            ? item.video.split("v=")[1]?.split("&")[0]
                                            : item.video.split("youtu.be/")[1]?.split("?")[0];
                                          return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}`;
                                        } else if (item.video.includes("vimeo.com")) {
                                          const videoId = item.video.split("vimeo.com/")[1]?.split("?")[0];
                                          return `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1`;
                                        }
                                        return item.video;
                                      })()
                                      }
                                      className="w-full h-full"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                      title={item.title}
                                    />
                                  ) : (item.image && (item.image.includes('youtube.com') || item.image.includes('youtu.be') || item.image.includes('vimeo.com'))) ? (
                                    <iframe
                                      src={(() => {
                                        if (item.image.includes("youtube.com") || item.image.includes("youtu.be")) {
                                          const videoId = item.image.includes("youtube.com")
                                            ? item.image.split("v=")[1]?.split("&")[0]
                                            : item.image.split("youtu.be/")[1]?.split("?")[0];
                                          return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}`;
                                        } else if (item.image.includes("vimeo.com")) {
                                          const videoId = item.image.split("vimeo.com/")[1]?.split("?")[0];
                                          return `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1`;
                                        }
                                        return item.image;
                                      })()
                                      }
                                      className="w-full h-full"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                      title={item.title}
                                    />
                                  ) : null}
                                </div>
                              ) : (
                                <img
                                  src={item.image_file || item.image}
                                  alt={item.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              )
                            ) : (
                              // Fallback for news without media
                              <div className="w-full h-full bg-gradient-to-br from-primary/10 to-primary/20 flex items-center justify-center">
                                <FontAwesomeIcon icon={faNewspaper} className="text-primary/40 text-4xl" />
                              </div>
                            )}
                          </div>

                          <div className="flex-1 p-6">
                            <div className="flex items-center gap-3 mb-3 flex-wrap">
                              <span className="flex items-center text-sm text-gray-500 font-nunito-sans">
                                <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-2 text-primary" />
                                {formatDate(item.created_at, i18n.language)}
                              </span>
                              <span className="flex items-center text-sm text-gray-500 font-nunito-sans">
                                <FontAwesomeIcon icon={faEye} className="w-4 h-4 mr-2 text-primary" />
                                {(item.views || 0).toLocaleString()} {(item.views || 0) === 1 ? t("news.view", "view") : t("news.views", "views")}
                              </span>
                              <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium font-nunito-sans">
                                {item.category?.name || "Uncategorized"}
                              </span>
                            </div>

                            <h2 className="font-catamaran text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-primary transition-colors duration-300">
                              {item.title}
                            </h2>

                            <p className="font-nunito-sans text-gray-600 text-sm line-clamp-3 leading-relaxed">
                              {stripHtml(item.description)}
                            </p>

                            <div className="mt-4 flex items-center justify-between">
                              <span className="text-primary font-nunito-sans text-sm font-medium group-hover:underline">
                                Read More
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-16"
                  >
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-gray-300 text-6xl mb-4" />
                    <h3 className="font-catamaran text-xl font-bold text-gray-700 mb-2">
                      {searchQuery ? "No Results Found" : "No News Available"}
                    </h3>
                    {searchQuery ? (
                      <div className="font-nunito-sans text-gray-500">
                        <p className="mb-2">
                          No results found for <span className="font-semibold">'{truncateText(searchQuery, 50)}'</span>
                        </p>
                      </div>
                    ) : (
                      <p className="font-nunito-sans text-gray-500">
                        Check back later for the latest news and updates.
                      </p>
                    )}
                  </motion.div>
                )}
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                previousLabel={t("common.previous")}
                nextLabel={t("common.next")}
              />

            </div>

            <Sidebar
              primaryItems={alerts}
              secondaryItems={events}
              loading={loading}
              primaryTitle="alerts"
              secondaryTitle="events"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default News;